"use client";

/**
 * AnalyticsGroupedTable - A unified table component for analytics dashboards
 *
 * Key improvements over GroupedTableOrchestrator:
 * 1. Single API call - All grouped data is returned in one query
 * 2. Better UX - Single table with expandable group rows (not separate tables per group)
 * 3. Built-in totals - Group summaries and grand totals are part of the data structure
 *
 * Data Flow:
 * - Backend returns: { groups: [...], grandTotals: {...} }
 * - Each group contains: { key, label, summary: {...}, rows: [...] }
 * - The table displays groups as expandable rows showing the summary
 * - Expanding a group reveals the detail rows nested underneath
 */
import { Table, TableBody } from "../index";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import Button from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import PlaceholderRow from "@/components/ui/table/placeholder-row";
import { cx } from "@/lib/tailwind-utils";
import { AnalyticsGroupedTableProvider } from "./context";
import { useExpandedGroups, useGroupPagination } from "./hooks";
import { AnalyticsFlatTableBody, AnalyticsGroupedTableBody, AnalyticsTableHeader } from "./table-body";
import type { AnalyticsFilters, AnalyticsGroupedResponse, AnalyticsGroupedTableProps, AnalyticsRow, AnalyticsSummary, ExpandCollapseControlsProps } from "./types";

// ============================================================================
// Main Component
// ============================================================================

export function AnalyticsGroupedTable<TRow extends AnalyticsRow = AnalyticsRow, TSummary extends AnalyticsSummary = AnalyticsSummary, TFilters extends AnalyticsFilters = AnalyticsFilters>({
  columns,
  filters,
  queryKey,
  queryFn,
  isGrouped,
  itemLabel = "items",
  showGrandTotals = true,
  grandTotalsPosition = "bottom",
  isTrailingGroup,
  defaultExpanded = false,
  onRowClick,
  getRowKey,
  loadingRowCount = 10,
  emptyMessage = "No data found",
  enabled = true,
  className,
  tableClassName,
  sortConfig,
  onSort,
  renderGroupHeader,
  renderSummaryLabel,
  tableRef,
  groupedColumnLabel,
  onExpansionChange,
  pagination
}: AnalyticsGroupedTableProps<TRow, TSummary, TFilters>) {
  // Pagination hook (only used when pagination prop is set)
  const paginationResult = useGroupPagination<TRow, TSummary, TFilters>({
    baseQueryKey: queryKey,
    queryFn: queryFn as Parameters<typeof useGroupPagination<TRow, TSummary, TFilters>>[0]["queryFn"],
    filters,
    pageSize: pagination ?? 10,
    enabled: enabled && !!pagination,
    placeholderScope: isGrouped
  });

  // Regular query (only used when pagination is not set)
  const regularQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await queryFn(filters);
      // Without pagination params, the queryFn always returns AnalyticsGroupedResponse
      // We narrow the type here since we know pagination isn't being used
      if (!result || !("groups" in result)) {
        return undefined;
      }
      return result as AnalyticsGroupedResponse<TRow, TSummary>;
    },
    enabled: enabled && !pagination
  });

  // Use appropriate result based on pagination mode
  // Both hooks return AnalyticsGroupedResponse | undefined (never GroupPageResponse)
  const data = pagination ? paginationResult.data : regularQuery.data;
  const isLoading = pagination ? paginationResult.isLoading : regularQuery.isLoading;
  const error = pagination ? paginationResult.error : regularQuery.error;

  // Get group keys for expansion management
  const groupKeys = useMemo(() => data?.groups.map(g => g.key) ?? [], [data?.groups]);

  // Use the extracted hook for expansion management
  const {
    expandedGroups,
    toggleGroup,
    expandAll,
    collapseAll,
    isAllExpanded,
    isAllCollapsed
  } = useExpandedGroups({
    groupKeys,
    defaultExpanded,
    onExpansionChange
  });

  // Reset expanded state when grouping mode changes (not when data updates)
  const prevIsGrouped = useRef(isGrouped);
  useEffect(() => {
    // Only reset when isGrouped actually changes, not on every data update
    if (prevIsGrouped.current !== isGrouped) {
      prevIsGrouped.current = isGrouped;
      if (defaultExpanded && data?.groups) {
        expandAll();
      } else {
        collapseAll();
      }
    }
  }, [isGrouped, defaultExpanded, data?.groups, expandAll, collapseAll]);

  // Expose expand/collapse functions via ref
  useImperativeHandle(tableRef, () => ({
    expandAll,
    collapseAll,
    expandedCount: expandedGroups.size,
    totalGroups: data?.groups.length ?? 0,
    isAllExpanded,
    isAllCollapsed
  }), [expandAll, collapseAll, expandedGroups.size, data?.groups.length, isAllExpanded, isAllCollapsed]);

  // Filter visible columns
  const visibleColumns = useMemo(() => columns.filter(col => col.visible !== false), [columns]);

  // Sort data if sortConfig is provided
  const sortedData = useMemo(() => {
    if (!data || !sortConfig) return data;

    // Find the column with a sortValue accessor for the current sort key
    const sortColumn = visibleColumns.find(c => (c.sortField ?? c.key) === sortConfig.key);
    const sortAccessor = sortColumn?.sortValue;
    const sortedGroups = data.groups.map(group => ({
      ...group,
      rows: [...group.rows].sort((a, b) => {
        // Use sortValue accessor if available, otherwise fall back to direct property access
        const aVal = sortAccessor ? sortAccessor(a) : (a as Record<string, unknown>)[sortConfig.key];
        const bVal = sortAccessor ? sortAccessor(b) : (b as Record<string, unknown>)[sortConfig.key];
        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        const comparison = aVal < bVal ? -1 : 1;
        return sortConfig.direction === "asc" ? comparison : -comparison;
      })
    }));
    return {
      ...data,
      groups: sortedGroups
    };
  }, [data, sortConfig, visibleColumns]);

  // Create sort filters adapter for SortableTableHead
  const sortFilters = useMemo(() => ({
    sortBy: sortConfig?.key ?? "",
    sortDirection: sortConfig?.direction ?? "desc" as const
  }), [sortConfig]);
  const setSortFilters = useCallback((update: typeof sortFilters | ((prev: typeof sortFilters) => typeof sortFilters)) => {
    const newFilters = typeof update === "function" ? update(sortFilters) : update;
    if (newFilters.sortBy && onSort) {
      // Only call onSort if the sort field changed or direction toggled
      onSort(newFilters.sortBy);
    }
  }, [sortFilters, onSort]);

  // Build context value
  const contextValue = useMemo(() => ({
    visibleColumns,
    sortedData,
    isGrouped,
    itemLabel,
    showGrandTotals,
    grandTotalsPosition,
    isTrailingGroup,
    emptyMessage,
    expandedGroups,
    toggleGroup,
    sortFilters,
    setSortFilters,
    onRowClick,
    getRowKey,
    renderSummaryLabel,
    renderGroupHeader,
    pagination: pagination ? {
      getGroupPage: paginationResult.getGroupPage,
      setGroupPage: paginationResult.setGroupPage,
      isGroupLoading: paginationResult.isGroupLoading,
      pageSize: pagination
    } : undefined
  }), [visibleColumns, sortedData, isGrouped, itemLabel, showGrandTotals, grandTotalsPosition, isTrailingGroup, emptyMessage, expandedGroups, toggleGroup, sortFilters, setSortFilters, onRowClick, getRowKey, renderSummaryLabel, renderGroupHeader, pagination, ...(pagination ? [paginationResult.getGroupPage, paginationResult.setGroupPage, paginationResult.isGroupLoading] : [])]);

  // Loading state
  if (isLoading) {
    return <div className={cx("min-w-0 overflow-x-auto", className)}>
        <AnalyticsGroupedTableProvider value={contextValue}>
          <Table className={cx("", tableClassName)}>
            <AnalyticsTableHeader groupedColumnLabel={groupedColumnLabel} />
            <TableBody>
              <PlaceholderRow rows={loadingRowCount} headers={visibleColumns.length + (isGrouped ? 1 : 0)} />
            </TableBody>
          </Table>
        </AnalyticsGroupedTableProvider>
      </div>;
  }

  // Error state
  if (error) {
    return <div className={cx("min-w-0 overflow-x-auto", className)}>
        <AnalyticsGroupedTableProvider value={contextValue}>
          <Table className={cx("", tableClassName)}>
            <AnalyticsTableHeader groupedColumnLabel={groupedColumnLabel} />
            <TableBody>
              <tr>
                <td colSpan={visibleColumns.length + (isGrouped ? 1 : 0)} className="py-8 text-center text-status-error-text">
                  Error loading data
                </td>
              </tr>
            </TableBody>
          </Table>
        </AnalyticsGroupedTableProvider>
      </div>;
  }
  return <div className={cx("min-w-0 overflow-x-auto", className)}>
      <AnalyticsGroupedTableProvider value={contextValue}>
        <Table className={cx("", tableClassName)}>
          <AnalyticsTableHeader groupedColumnLabel={groupedColumnLabel} />
          {isGrouped ? <AnalyticsGroupedTableBody<TRow, TSummary> /> : <AnalyticsFlatTableBody<TRow, TSummary> />}
        </Table>
      </AnalyticsGroupedTableProvider>
    </div>;
}

// ============================================================================
// Expand/Collapse Controls Component
// ============================================================================

/**
 * Standalone expand/collapse controls for AnalyticsGroupedTable
 * Place this component wherever you want the controls to appear
 *
 * For optimal performance, use the `expansionState` prop with the `onExpansionChange`
 * callback from AnalyticsGroupedTable to avoid polling.
 */
export function ExpandCollapseControls({
  tableRef,
  show = true,
  expansionState
}: ExpandCollapseControlsProps) {
  // Get values from expansionState prop or fall back to ref
  const totalGroups = expansionState?.totalGroups ?? tableRef.current?.totalGroups ?? 0;
  const isAllExpanded = expansionState?.isAllExpanded ?? tableRef.current?.isAllExpanded ?? false;
  const isAllCollapsed = expansionState?.isAllCollapsed ?? tableRef.current?.isAllCollapsed ?? true;
  if (!show || !tableRef.current || totalGroups <= 1) {
    return null;
  }
  const {
    expandAll,
    collapseAll
  } = tableRef.current;
  return <ButtonGroup>
      <Button variant="secondary" onClick={expandAll} disabled={isAllExpanded}>
        Expand All
      </Button>
      <Button variant="secondary" onClick={collapseAll} disabled={isAllCollapsed}>
        Collapse All
      </Button>
    </ButtonGroup>;
}
export default AnalyticsGroupedTable;